import type { FormEventHandler } from 'react';
import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Form, useRouteLoaderData } from '@remix-run/react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import cx from 'classnames';

import Button from '~/components/Button';
import { useWebThemeContext } from '~/components/Layout/Context/useWebThemeContext';
import useCreateStripePriceSession from '~/hooks/useCreateStripePriceSession';
import useEmail from '~/hooks/useEmail';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';

import EmailCaptureModal from './PlanCards/components/EmailCaptureModal';

import EmailConfirmationModal from './EmailConfirmationModal';
import PremiumMemberModal from './PremiumMemberModal';

interface PayFormProps {
  priceId: string;
  onContinue: () => void;
  cardRequired?: boolean;
  text?: string | React.ReactNode;
  buttonStyle?: React.CSSProperties;
  fontFamily?: string;
  premiumOfferId?: string;
  isCollectingBillingAddress?: boolean;
  email: string;
}

const PayForm = ({
  priceId,
  cardRequired = true,
  text,
  onContinue,
  buttonStyle,
  fontFamily,
  premiumOfferId = '',
  isCollectingBillingAddress = false,
  email = '',
}: PayFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const { classes } = useWebThemeContext();
  const isLoggedIn = useIsLoggedIn();
  const [storedEmail] = useEmail();
  const rootData = useRouteLoaderData<any>('root');
  const subscriberId = rootData?.subscription?.subscriber_id;
  const redirectToStripe = cardRequired;
  const [isEmailCaptureModalOpen, setIsEmailCaptureModalOpen] = React.useState(false);
  const [isEmailConfirmationModalOpen, setIsEmailConfirmationModalOpen] = React.useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = React.useState(false);
  const [pendingSessionId, setPendingSessionId] = React.useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { mutateAsync: createStripePriceSession } = useCreateStripePriceSession();

  // Use stored email if available, fallback to prop
  const effectiveEmail = storedEmail || email;

  const redirectToCheckout = async (sessionId: string) => {
    if (!stripe || !elements) {
      return;
    }

    const response = await stripe.redirectToCheckout({
      sessionId: sessionId,
    });
    if (!response.error) {
      return; // all good
    }
    throw new Error('Stripe checkout failed');
  };

  const handleStripeSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    if (isLoggedIn) {
      handleEmailConfirmed(effectiveEmail);
      return;
    }

    setIsEmailConfirmationModalOpen(true);
  };

  const handleEmailConfirmed = async (confirmedEmail: string) => {
    if (!stripe || !elements) {
      setIsSubmitting(false);
      setIsEmailConfirmationModalOpen(false);
      return;
    }

    try {
      const sessionResponse = await createStripePriceSession(
        priceId,
        confirmedEmail,
        premiumOfferId,
        isCollectingBillingAddress,
      );

      if (sessionResponse) {
        // Check if this is a duplicate subscription
        if (sessionResponse.is_duplicate) {
          setPendingSessionId(sessionResponse.session_id);
          setIsEmailConfirmationModalOpen(false);
          setIsDuplicateModalOpen(true);
          setIsSubmitting(false);
          return;
        }

        // Not a duplicate, proceed normally
        await redirectToCheckout(sessionResponse.session_id);
        return;
      }
    } catch (error) {
      console.error(error);
    }
    // if we reach here, something went wrong
    toast.error('Something went wrong while creating a Stripe session, please refresh the page and try again');
    setIsSubmitting(false);
    setIsEmailConfirmationModalOpen(false);
  };

  const handleDuplicateContinue = async () => {
    if (pendingSessionId) {
      try {
        setIsSubmitting(true);
        await redirectToCheckout(pendingSessionId);
      } catch (error) {
        console.error(error);
        toast.error('Something went wrong while redirecting to Stripe, please try again');
        setIsSubmitting(false);
      }
    }
  };

  const handleSwarmSubmit = async (_event: any) => {
    setIsEmailCaptureModalOpen(true);
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();

    if (redirectToStripe) {
      handleStripeSubmit(event);
    } else {
      handleSwarmSubmit(event);
    }
  };

  return (
    <>
      <Form method="post" onSubmit={handleSubmit}>
        <Button
          type="submit"
          variant="primary"
          className={cx('flex w-full justify-center', classes.radius, fontFamily)}
          disabled={isSubmitting}
          style={buttonStyle}
        >
          {text || t('shared.cta.get_access')}
        </Button>
      </Form>
      <EmailCaptureModal
        isOpen={isEmailCaptureModalOpen}
        onClose={() => {
          setIsEmailCaptureModalOpen(false);
        }}
        onConfirm={() => {
          setIsEmailCaptureModalOpen(false);
          onContinue();
        }}
        premiumOfferId={premiumOfferId}
      />
      <PremiumMemberModal
        isUpgradeActive={isDuplicateModalOpen}
        onContinue={handleDuplicateContinue}
        onClose={() => setIsDuplicateModalOpen(false)}
        subscriberId={subscriberId}
      />
      <EmailConfirmationModal
        isOpen={isEmailConfirmationModalOpen}
        onClose={() => {
          setIsEmailConfirmationModalOpen(false);
          setIsSubmitting(false);
        }}
        onConfirm={handleEmailConfirmed}
        initialEmail={effectiveEmail}
      />
    </>
  );
};

export default PayForm;
